import { productForOrgKind } from '@app/shared/brand'
import { useQuery } from '@tanstack/react-query'
import {
  currentSite,
  faceOrigin,
  type PublicRoute,
  type Site,
} from './public-router'
import { useTRPC } from './trpc'

/**
 * Which product's host an org's public page belongs on, when it was asked for
 * on another site — or null where it is already home.
 *
 * An org's product follows from its kind, and its pages live on that product's
 * host: a charity addressed on the shul product's host or the platform's would
 * be dressed as something it is not, and a shul's printed links name the shul
 * product and must keep landing there. A congregation's own domain is answered
 * where it was asked: it is nobody's product, and which org it serves is the
 * domain's to say rather than this.
 */
export function orgBelongsElsewhere(
  here: Site,
  orgKind: string,
): ReturnType<typeof productForOrgKind> | null {
  if (here === 'org') return null
  const own = productForOrgKind(orgKind)
  return own === here ? null : own
}

/**
 * The origin an org's public page should be relocated to, read off the org.
 *
 * `pending` while the org is still being read, so nothing of it is drawn in the
 * wrong chrome first; null where it is home, where the route names no org, and
 * where the id resolves nothing — that is the page's own not-found to give. A
 * failed read is not retried: these pages include a pay link's, and somebody
 * paying waits on every retry with nothing on the screen.
 */
export function useOrgElsewhere(route: PublicRoute): string | null | 'pending' {
  const trpc = useTRPC()
  const here = currentSite()
  const orgId = route.kind === 'org' ? route.orgId : ''
  const asks = !!orgId && here !== 'org'
  const orgs = useQuery({
    ...trpc.org.list.queryOptions(),
    enabled: asks,
    retry: false,
  })
  if (!asks) return null
  if (orgs.isLoading) return 'pending'
  const org = orgs.data?.find((o) => o.shortId === orgId)
  if (!org) return null
  const own = orgBelongsElsewhere(here, org.kind)
  return own ? faceOrigin(own, 'public') : null
}
